/* eslint-disable no-duplicate-imports, no-unused-vars, react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { CircularProgress, ListItem, Box, Chip } from "@mui/material";
import { useMsal } from "@azure/msal-react";
import SupplierTable from "../../components/table/supplierTable";
import MultiSelectWithSearchInput2 from "../../components/dropdown/MultiSelectWithSearchInput2";

import BarAndLineChartSupplierInsights from "Samarth Module/components/barAndLineComboChart/barAndLineChartSupplierInsight";
import {
    supplierInsightsAPI,
    vendorOtifAPI,
    vendorQCAPI,
    overduePOAPI,
    leadTimeAnalysisAPI,
    sobComplianceAPI,
    exportToExcelSupplierFile,
    getOptimizedFilterDetails
} from "Samarth Module/api/api";
import resetBtn from "../../../assets/images/resetBtn.png";
import ExportLoader from "../../components/snackbar/exportLoader";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import SupplierArrowIcon from "../../../assets/images/supplierArrow.svg";
import ContributionRange from "Samarth Module/components/progressBar/contributionRange";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import Filters from "../../../assets/images/filters.svg";
import Excel from "../../../assets/images/Excel.svg";
import MoreFilters from "../../../Samarth Module/components/drawers/moreFilters";

import { getAlertConfig } from "../../constants/alertConfig";
import DateRange from "Samarth Module/components/dropdown/dateRangePicker";
import dayjs from "dayjs";
import styles from "./alert.module.css";
import Tabs from "Agentic Module/components/cards/tabs";
import EmailTriagePage from "Agentic Module/pages/emailTriaging/emailTriaging";
import TntLinkagePage from "Agentic Module/pages/tntLinkage/tntLinkage";
import EmailValidationTab from "Agentic Module/pages/hitl/hitl";
import { useLocation } from "react-router-dom";

const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

// API mapping
const API_MAP = {
    supplier: supplierInsightsAPI,
    otif: vendorOtifAPI,
    qc: vendorQCAPI,
    po: overduePOAPI,
    lead: leadTimeAnalysisAPI,
    sob: sobComplianceAPI
};

const DATA_KEYS = [
    { key: "otif", title: "Vendor OTIF" },
    { key: "qc", title: "Vendor QC index" },
    { key: "po", title: "Over due PO" },
    { key: "lead", title: "Lead time Analysis" },
    { key: "sob", title: "SOB compliance" },
];

const SectionWithLoader = React.memo(function SectionWithLoader({
    title,
    sectionKey,
    data,
    pagination,
    itemsPerPage,
    loadingState,
    allFilters,
    rmpmFiltersOption,
    poDetails,
    fetchSectionData
}) {
    const displayData = useMemo(() => {
        return data.map(item => {
            const newItem = { ...item };
            return newItem;
        });
    }, [data]);

    const handlePageChange = (event, newPage) => {
        fetchSectionData(sectionKey, newPage + 1, pagination.per_page, "pagination");
    };

    const handleRowsPerPageChange = (event) => {
        const newPerPage = Number.parseInt(event.target.value, 10);
        fetchSectionData(sectionKey, 1, newPerPage, "pagination");
    };

    return (
        <div className={styles.vendorSubContainer}>
            <div className={styles.vendorSubHeader}>
                <span className={styles.vendorText}>{title}</span>
            </div>
            {loadingState?.loading &&
                (loadingState?.reason === "initial" || loadingState?.reason === "range-change") ? (
                <div className={styles.supplierLoaderContainer}><CircularProgress /></div>
            ) : (
                <SupplierTable
                    data={displayData}
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    filters={allFilters}
                    rmpmFiltersOption={rmpmFiltersOption}
                    poDetails={poDetails}
                    tableType={sectionKey}
                />
            )}
        </div>
    );
});

SectionWithLoader.propTypes = {
    title: PropTypes.string,
    sectionKey: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
    pagination: PropTypes.shape({
        page: PropTypes.number,
        per_page: PropTypes.number,
        total_count: PropTypes.number,
    }),
    itemsPerPage: PropTypes.number,
    loadingState: PropTypes.shape({
        loading: PropTypes.bool,
        reason: PropTypes.string,
    }),
    allFilters: PropTypes.arrayOf(PropTypes.object),
    rmpmFiltersOption: PropTypes.object,
    poDetails: PropTypes.object,
    fetchSectionData: PropTypes.func,
};

SectionWithLoader.defaultProps = {
    title: "",
    sectionKey: "",
    data: [],
    pagination: {},
    itemsPerPage: 10,
    loadingState: { loading: false, reason: "" },
    allFilters: [],
    rmpmFiltersOption: {},
    poDetails: {},
    fetchSectionData: () => {},
};

const SupplierInsights = ({ alertType }) => {
    const location = useLocation();
    const { accounts } = useMsal();
    const activeAccount = accounts[0] || {};
    const username = activeAccount.username;
    const itemsPerPage = 10;
    const { uiConfig } = getAlertConfig(alertType);

    const [loading, setLoading] = useState({
        filters: true,
        contributionRange: true,
        supplier: { loading: true, reason: "initial" },
        otif: { loading: true, reason: "initial" },
        qc: { loading: true, reason: "initial" },
        po: { loading: true, reason: "initial" },
        lead: { loading: true, reason: "initial" },
        sob: { loading: true, reason: "initial" },
    });


    const [supplierInsights, setSupplierInsights] = useState({});
    const [filteredDataMap, setFilteredDataMap] = useState({});
    const [paginationMap, setPaginationMap] = useState({});
    const toggles = useMemo(() => ({
        chart: true,
        supplier: true,
        otif: true,
        qc: true,
        po: true,
        lead: true,
        sob: true
    }), []);
    const [rmpmFilters, setRmpmFilters] = useState([]);
    const [allFilters, setAllFilters] = useState([]);
    const [rmpmFiltersOption, setRmpmFiltersOption] = useState({});
    const [filterChangeCount, setFilterChangeCount] = useState(0);
    const [contributionRange, setContributionRange] = useState(100);
    const latestFilters = useRef([]);
    const clearFiltersRef = useRef(false);
    const [exportLoader, setExportLoader] = useState(false);
    const [filterDrawer, setFilterDrawer] = useState(false);

    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: '',
        type: 'error'
    });


    const [dateRange, setDateRange] = useState({
        from: "2025-01-01",
        to: dayjs().format("YYYY-MM-DD"),
        fromDate: { day: "01", month: "January", year: "2025" },
        toDate: {
            day: dayjs().date().toString().padStart(2, "0"),
            month: months[dayjs().month()],
            year: `${dayjs().year()}`
        }
    });


    // Transform filters for API payload
    const transformFilters = useCallback((filters) => {
        return filters.reduce((acc, filter) => {
            if (filter.selectedData?.length > 0) {
                acc[filter.label] = filter.selectedData;
            }
            return acc;
        }, {});
    }, []);

    // Generate Tables API payload
    const generatePayload = useCallback((page, perPage, sectionKey, newRange) => {
        const shouldClearFilters = clearFiltersRef.current;
        clearFiltersRef.current = false;
        
        return {
            email: username,
            type: alertType,
            page,
            per_page: perPage,
            filterData: transformFilters(latestFilters.current),
            rangeThreshold: newRange ?? contributionRange,
            // toggles: {
            //     [sectionKey]: toggles[sectionKey]
            // },
            dateRange: dateRange,
            clearFilters: shouldClearFilters
        };
    }, [username, alertType, transformFilters, contributionRange, dateRange]);

    // [username, alertType, transformFilters, toggles, contributionRange, dateRange]);

    // Generate Supplier performance API payload
    const supplierPayload = useCallback((newRange) => {
        const shouldClearFilters = clearFiltersRef.current;
        clearFiltersRef.current = false;
        
        const effectiveRange = newRange !== null && newRange !== undefined ? newRange : contributionRange;

        return {
            email: username,
            type: alertType,
            filterData: transformFilters(latestFilters.current),
            // toggles: {
            //     supplier: toggles.supplier
            // },
            rangeThreshold: effectiveRange,
            dateRange: dateRange,
            clearFilters: shouldClearFilters
        }

    }, [username, alertType, transformFilters, contributionRange, dateRange]);
    //  [username, alertType, transformFilters, toggles.supplier, contributionRange, dateRange]);

    const fetchFilterDetails = useCallback(async () => {
        return getOptimizedFilterDetails(alertType, username);
    }, [username, alertType]);


    const initializeFilters = useCallback(async () => {
        try {
            const apiFiltersResponse = await fetchFilterDetails(alertType, username);

            // Process API response
            const orderedFilters = (apiFiltersResponse?.data?.filters || [])
                .sort((a, b) => a.displayOrder - b.displayOrder);

            const allFilterOptions = apiFiltersResponse?.data?.allFilterOptions || {};

            // Create visible favorites
            const visibleFilters = orderedFilters
                .filter(f => f.isFavorite)
                .map(f => ({
                    ...f,
                    selectedData: []
                }));

            // // Create all filters
            const allFilters = orderedFilters.map(f => ({
                ...f,
                selectedData: [],
                options: allFilterOptions[f.label] || []
            }));

            setAllFilters(allFilters);
            setRmpmFilters(visibleFilters);
            setRmpmFiltersOption(allFilterOptions);

            setLoading(prev => ({ ...prev, filters: false }));
        } catch (error) {
            console.error("Error initializing filters:", error);
            setLoading(prev => ({ ...prev, filters: false }));
        }
    }, [fetchFilterDetails]);

    // Fetch data for a specific section
    const fetchSectionData = useCallback(async (sectionKey, page = 1, perPage = itemsPerPage, reason = "initial", newRange = null) => {
        setLoading(prev => ({
            ...prev,
            [sectionKey]: { loading: true, reason }
        }));

        try {
            const payload = generatePayload(page, perPage, sectionKey, newRange);
            const apiFunction = API_MAP[sectionKey];

            if (!apiFunction) {
                throw new Error(`No API found for section: ${sectionKey}`);
            }

            const res = await apiFunction(payload);

            if (res) {
                setFilteredDataMap(prev => ({
                    ...prev,
                    [sectionKey]: res.alertDetails || [],
                    [`${sectionKey}_details`]: res.overduePoDetailData || {}
                }));

                setPaginationMap(prev => ({
                    ...prev,
                    [sectionKey]: res.pagination || {
                        total: res.alertDetails?.length || 0,
                        page,
                        per_page: perPage,
                        total_pages: Math.ceil((res.alertDetails?.length || 0) / perPage)
                    }
                }));
            }
        } catch (err) {
            console.error(`Error fetching ${sectionKey} data:`, err);
        } finally {
            setLoading(prev => ({
                ...prev,
                [sectionKey]: { loading: false, isInitial: false }
            }));
        }
    }, [generatePayload]);

    const fetchSupplierData = useCallback((reason = "initial", newRange = null) => {
        setLoading(prev => ({
            ...prev,
            supplier: { loading: true, reason }
        }));

        setTimeout(async () => {
            try {
                const res = await supplierInsightsAPI(supplierPayload(newRange));
                setSupplierInsights(res || {});
            } catch (err) {
                console.error('Error fetching supplier data:', err);
            } finally {
                setLoading(prev => ({
                    ...prev,
                    supplier: { loading: false, reason }
                }));
            }
        }, 0);
    }, [supplierPayload]);


    useEffect(() => {
        initializeFilters();
        // Fetch data for all sections
        DATA_KEYS.forEach(({ key }) => {
            fetchSectionData(key);
        });
        fetchSupplierData();
    }, []);

    // Update latest filters ref
    useEffect(() => {
        latestFilters.current = allFilters;
    }, [allFilters]);

    useEffect(() => {
        if (filterChangeCount > 0 && dateRange.from && dateRange.to) {
            // Reset all tables to page 1
            DATA_KEYS.forEach(({ key }) => {
                fetchSectionData(key, 1, itemsPerPage);
            });
           
            fetchSupplierData("filter-change", contributionRange);
        }

    }, [filterChangeCount, fetchSectionData, fetchSupplierData, contributionRange, dateRange]);

    // Filter handlers
    const handleMultiSelectChange = useCallback((label, values) => {
        const updatedFilters = allFilters.map(filter =>
            filter.label === label ? { ...filter, selectedData: values } : filter
        );
        
        setAllFilters(updatedFilters);

        // Check if there are any selected filters
        const hasSelection = updatedFilters.some((filter) => {
            const selected = filter?.selectedData;
            if (Array.isArray(selected)) return selected.length > 0;
            if (typeof selected === 'string') return selected.trim().length > 0;
            return selected !== undefined && selected !== null;
        });
        
        clearFiltersRef.current = !hasSelection;
        setFilterChangeCount(prev => prev + 1);
    }, [allFilters]);

    // Reset filters handler
    const handleResetFilters = useCallback(() => {
        clearFiltersRef.current = true;
        const defaultContributionRange = 100;

        // Reset ALL filters (both favorite and non-favorite)
        const resetAllFilters = allFilters.map(filter => ({
            ...filter,
            selectedData: []
        }));

        // Reset favorite filters
        const resetRmpmFilters = rmpmFilters.map(filter => ({
            ...filter,
            selectedData: []
        }));

        // Update both states
        setAllFilters(resetAllFilters);
        setRmpmFilters(resetRmpmFilters);
        latestFilters.current = resetAllFilters; // Or resetAllFilters if you want all filters to affect data

        // Reset date range
        const defaultDateRange = {
            from: "2025-01-01",
            to: dayjs().format("YYYY-MM-DD"),
            fromDate: { day: "01", month: "January", year: "2025" },
            toDate: {
                day: dayjs().date().toString().padStart(2, "0"),
                month: months[dayjs().month()],
                year: `${dayjs().year()}`
            }
        };
        setDateRange(defaultDateRange);

        // Reset contribution range
        setContributionRange(defaultContributionRange);

        // Re-fetch data for all sections
        DATA_KEYS.forEach(({ key }) => {
            fetchSectionData(key, 1, itemsPerPage, "reset", defaultContributionRange);
        });

        // Re-fetch supplier chart data
        fetchSupplierData("reset", defaultContributionRange);
    }, [allFilters, rmpmFilters, fetchSectionData, fetchSupplierData]);


    // Chart data 
    const chartData = useMemo(() => {
        if (!supplierInsights?.alertDetails) return [];
        return supplierInsights.alertDetails.Value || [];
    }, [supplierInsights]);
    //     return toggles.chart
    //         ? supplierInsights.alertDetails.Value || [] 
    //      : supplierInsights.alertDetails.Volume || [];
    // }, [supplierInsights, toggles.chart]);

    // Keep previous toggle states
    const previousToggles = useRef({ ...toggles });

    // Effect to handle toggle changes
    useEffect(() => {
        DATA_KEYS.forEach(({ key }) => {
            if (previousToggles.current[key] !== toggles[key]) {
                const currentPage = paginationMap[key]?.page || 1;
                const perPage = paginationMap[key]?.per_page || itemsPerPage;

                fetchSectionData(key, currentPage, perPage, "toggle");
                previousToggles.current[key] = toggles[key];
            }
        });

        if (previousToggles.current.supplier !== toggles.supplier) {
            fetchSupplierData("toggle", contributionRange);
            previousToggles.current.supplier = toggles.supplier;
        }
    }, [toggles, fetchSectionData, fetchSupplierData, paginationMap]);

    // Contribution Range Change Handler
    const handleRangeChange = (newRange) => {
        setContributionRange(newRange);
        clearFiltersRef.current = false; // Contribution range is a filter

        // Re-fetch all data tables based on new range
        DATA_KEYS.forEach(({ key }) => {
            fetchSectionData(key, 1, itemsPerPage, "range-change", newRange);
        });

        // Optionally refetch chart data if needed
        fetchSupplierData("range-change", newRange);
    };

    const exportToExcel = useCallback(async () => {

        const payload = {
            email: username,
            type: alertType,
            filters: transformFilters(latestFilters.current),
            dateRange: dateRange,
            contributionRange: contributionRange,
            // toggles: { otif: true, qc: true, po: true, lead: true }
        };
        try {
            setExportLoader(true);
            await exportToExcelSupplierFile(payload)
        } catch (error) {
            setSnackBarObject({ snackbarOpen: true, message: error.message, type: 'error' });
        } finally {
            setExportLoader(false);
        }
    });

    const handleFilterDrawer = useCallback(() => {
        setFilterDrawer(prev => !prev);
    }, []);

    const handleChipDelete = useCallback((filterLabel, selectedValue) => {
        const updatedAllFilters = allFilters.map(f =>
            f.label === filterLabel
                ? { ...f, selectedData: f.selectedData.filter(v => v !== selectedValue) }
                : f
        );

        setAllFilters(updatedAllFilters);

        if (rmpmFilters.some(f => f.label === filterLabel)) {
            const updatedRmpmFilters = rmpmFilters.map(f =>
                f.label === filterLabel
                    ? { ...f, selectedData: f.selectedData.filter(v => v !== selectedValue) }
                    : f
            );
            setRmpmFilters(updatedRmpmFilters);
            latestFilters.current = updatedRmpmFilters;
        }

        setFilterChangeCount(prev => prev + 1);
    }, [allFilters, rmpmFilters]);

    const handleFiltersSaved = useCallback((updatedFilters) => {
        // Update all filters (for the drawer)
        setAllFilters(updatedFilters);

        // Update visible filters (only favorites)
        const visibleFilters = updatedFilters.filter(f => f.isFavourite);
        setRmpmFilters(visibleFilters);

        // Update latestFilters ref for API calls
        latestFilters.current = visibleFilters;

        // Check if there are any selected filters
        const hasSelection = updatedFilters.some((filter) => {
            const selected = filter?.selectedData;
            if (Array.isArray(selected)) return selected.length > 0;
            if (typeof selected === 'string') return selected.trim().length > 0;
            return selected !== undefined && selected !== null;
        });
        
        clearFiltersRef.current = !hasSelection;

        // Trigger a re-fetch
        setFilterChangeCount(prev => prev + 1);
    }, []);

    const closeSnackbar = useCallback(() => {
        setSnackBarObject({ message: '', snackbarOpen: false, type: 'error' });
    }, []);

    const supplierAnalyticsTab = {
        label: "Supplier Analytics", content: <>
                <div className={styles.rmpmTableBody}>


                    {/* Filters Section */}
                    <div className={styles.filters}
                        style={{
                            width: '100%', display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap'
                        }}>
                        {rmpmFilters.map(filter => (
                            <ListItem key={filter.label} className={styles.listCustomClass}>
                                <MultiSelectWithSearchInput2
                                    key={filter.label}
                                    label={filter.label}
                                    options={rmpmFiltersOption[filter.label] || []}
                                    filterValues={filter.selectedData}
                                    handleFilter={handleMultiSelectChange}
                                    disabled={false}
                                    isLabelShow={true}
                                    type={alertType}
                                />
                            </ListItem>
                        ))}
                        <ListItem className={styles.listCustomClass}>
                            <div className={styles.dateRangeContainer}>
                                <span className={styles.dateRangeText}>Date Range</span>
                                <DateRange
                                    value={{
                                        fromDate: dateRange.fromDate,
                                        toDate: dateRange.toDate
                                    }}
                                    onChange={(range) => {
                                        setDateRange(prev => ({
                                            ...prev,
                                            from: range.from,
                                            to: range.to,
                                            // Rebuild structured date objects
                                            fromDate: { day: "01", month: "January", year: "2025" },
                                            toDate: {
                                                day: dayjs(range.to).date().toString().padStart(2, "0"),
                                                month: months[dayjs(range.to).month()],
                                                year: `${dayjs(range.to).year()}`
                                            }
                                        }));
                                        clearFiltersRef.current = false; // Date range is a filter
                                        setFilterChangeCount(prev => prev + 1);
                                    }}
                                />
                            </div>
                        </ListItem>
                        <div style={{ flex: 1 }} />
                        <div className={styles.overFlowMenu}>
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                style={{ cursor: 'pointer', marginRight: '10px', background: 'transparent', border: 'none', padding: 0 }}
                                aria-label="Reset Filters"
                            >
                                <img
                                    src={resetBtn}
                                    alt="Reset Filters"
                                />
                            </button>

                            <OverflowMenu
                                menuItems={[
                                    {
                                        label: 'More Filters',
                                        icon: Filters,
                                        onClick: () => handleFilterDrawer()
                                    },
                                    {
                                        label: 'Export',
                                        icon: Excel,
                                        onClick: exportToExcel,
                                    },
                                ]}
                            />
                        </div>
                    </div>
                    <div className={styles.filtersBtnContainer}>
                        <Box className={styles.chipContainer}>
                            {/* Show chips ONLY for non-favorite selected filters */}
                            {allFilters
                                .filter(filter => {
                                    // Double-check this filter is NOT a favorite
                                    const isFavorite = rmpmFilters.some(f => f.label === filter.label);
                                    return !isFavorite && filter.selectedData?.length > 0;
                                })
                                .map((filter) => (
                                    filter.selectedData.map((selectedValue, index) => (
                                        <Chip
                                            key={`${filter.label}-${index}`}
                                            label={selectedValue}
                                            onDelete={() => handleChipDelete(filter.label, selectedValue)}
                                        />
                                    ))
                                ))}
                        </Box>
                        <ContributionRange value={contributionRange} onRangeChange={handleRangeChange} />
                    </div>

                    {/* Bar Chart Section */}
                    <div className={styles.boxContainer}>
                        <div className={styles.chartContainer}>
                            <span className={styles.supplierText}>Supplier performance index</span>
                            <img
                                src={SupplierArrowIcon}
                                alt="SupplierArrowIcon"
                                className={styles.supplierImage}
                            />
                            <div className={styles.toggle}>
                                {/* <ToggleSwitch
                                value={toggles.chart}
                                onToggle={handleToggle("chart")}
                            /> */}
                            </div>
                        </div>
                        {loading.supplier?.loading ? (
                            <div className={styles.supplierLoaderContainer}><CircularProgress /></div>
                        ) :
                            (
                                <>
                                    {chartData === null || chartData.length === 0 ? (
                                        <div className={styles.supplierMessage}>
                                            No supplier performance index data found
                                        </div>
                                    ) : (
                                        <BarAndLineChartSupplierInsights data={chartData} />
                                    )}
                                </>
                            )}
                    </div>
                    {/* Data Tables Section */}
                    <span className={styles.vendorSiteText}>Vendor Site view</span>
                    {DATA_KEYS.map(({ key, title }, index) => (
                        index % 2 === 0 && (
                            <div className={styles.vendorContainer} key={`row-${key}-${DATA_KEYS[index + 1]?.key || "none"}`}>
                                <SectionWithLoader
                                    title={title}
                                    sectionKey={key}
                                    data={filteredDataMap[key] || []}
                                    pagination={paginationMap[key] || {
                                        total: 0,
                                        page: 1,
                                        per_page: itemsPerPage,
                                        total_pages: 0
                                    }}
                                    itemsPerPage={itemsPerPage}
                                    loadingState={loading[key]}
                                    allFilters={allFilters}
                                    rmpmFiltersOption={rmpmFiltersOption}
                                    poDetails={filteredDataMap["po_details"] || {}}
                                    fetchSectionData={fetchSectionData}
                                />
                                {DATA_KEYS[index + 1] && (
                                    <SectionWithLoader
                                        title={DATA_KEYS[index + 1].title}
                                        sectionKey={DATA_KEYS[index + 1].key}
                                        data={filteredDataMap[DATA_KEYS[index + 1].key] || []}
                                        pagination={paginationMap[DATA_KEYS[index + 1].key] || {
                                            total: 0,
                                            page: 1,
                                            per_page: itemsPerPage,
                                            total_pages: 0
                                        }}
                                        itemsPerPage={itemsPerPage}
                                        loadingState={loading[DATA_KEYS[index + 1].key]}
                                        allFilters={allFilters}
                                        rmpmFiltersOption={rmpmFiltersOption}
                                        poDetails={filteredDataMap["po_details"] || {}}
                                        fetchSectionData={fetchSectionData}
                                    />
                                )}
                            </div>
                        )
                    ))}
                </div>
                {
                    uiConfig.showMoreFilters && filterDrawer && (
                        <MoreFilters
                            anchor="right"
                            filterDrawer={filterDrawer}
                            handleFilterDrawer={handleFilterDrawer}
                            rmpmFilters={allFilters.map(filter => ({
                                ...filter,
                                // This ensures stars match what's shown on main screen
                                isFavourite: rmpmFilters.some(f => f.label === filter.label)
                            }))}
                            setRmpmFilters={(updatedFilters) => {
                                // Update both filter states
                                setAllFilters(updatedFilters);
                                setRmpmFilters(updatedFilters.filter(f => f.isFavourite));
                            }}
                            username={activeAccount.username}
                            type={alertType}
                            options={rmpmFiltersOption}
                            onFiltersSaved={handleFiltersSaved}
                        />
                    )
                }
                {
                    snackBarObject.snackbarOpen && (
                        <SnackBarMessage
                            type={snackBarObject.type}
                            snackbarMessage={snackBarObject.message}
                            snackbarOpen={snackBarObject.snackbarOpen}
                            setSnackbarOpen={closeSnackbar}
                        />
                    )
                }
                {exportLoader && <ExportLoader />}
            </>
    };

       const monitorTodayTab = {
        label: "Monitor Today",
        content: <EmailTriagePage alertType={alertType} />,
    };
    const tntLinkageTab = {
        label: "TnT Linkage",
        content: <TntLinkagePage alertType={alertType} />,
    };
     const hitlTab = {
        label: "Email HITL",
        content: <EmailValidationTab alertType={alertType} />,
    };

    const tabs =[monitorTodayTab,tntLinkageTab,hitlTab, supplierAnalyticsTab];
    const initialTabLabel = location?.state?.initialTabLabel || "";

    return (
        <div className={styles.supplierContainer}>
            <Tabs tabs={tabs} initialTabLabel={initialTabLabel} />
        </div >
    );
};

SupplierInsights.propTypes = {
    alertType: PropTypes.string,
};

SupplierInsights.defaultProps = {
    alertType: "",
};

export default React.memo(SupplierInsights);






